import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, ThumbsUp, Sparkles, Trophy, Users, Vote as VoteIcon } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "./EmptyState";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { GibletGuide } from "./GibletGuide";
import { useSprintRealtime } from "@/hooks/useSprintRealtime";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";

interface DecideStageProps {
  sprintId: string;
  userId: string;
  onComplete: () => void;
}

interface Decision {
  id: string;
  decision_text: string;
  rationale: string;
  ai_confidence_score: number;
  contributor_votes: any;
  is_selected: boolean;
}

export const DecideStage = ({ sprintId, userId, onComplete }: DecideStageProps) => {
  const { toast } = useToast();
  const [decisions, setDecisions] = useState<Decision[]>([]);
  const [generating, setGenerating] = useState(false);
  const [loading, setLoading] = useState(true);
  const [milestone, setMilestone] = useState<string>('');

  // Real-time updates for voting
  useSprintRealtime(
    sprintId,
    (payload) => {
      if (payload.eventType === 'UPDATE') {
        setDecisions(prev => prev.map(d => 
          d.id === payload.new.id ? payload.new : d
        ));
      }
    }
  );

  useEffect(() => {
    loadDecisions();
  }, [sprintId]);

  const loadDecisions = async () => {
    try {
      const { data, error } = await supabase
        .from('sprint_decisions')
        .select('*')
        .eq('sprint_id', sprintId);

      if (error) throw error;

      if (!data || data.length === 0) {
        // No decisions yet, generate them
        await generateDecisions();
      } else {
        setDecisions(data as Decision[]);
      }
    } catch (error: any) {
      toast({
        title: "Failed to load decisions",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const generateDecisions = async () => {
    setGenerating(true);
    try {
      const { data, error } = await supabase.functions.invoke('generate-sprint-decisions', {
        body: { sprintId }
      });

      if (error) throw error;

      setDecisions(data.decisions as Decision[]);
      toast({ title: "AI decisions generated successfully" });
    } catch (error: any) {
      toast({
        title: "Failed to generate decisions",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setGenerating(false);
    }
  };

  const handleVote = async (decisionId: string) => {
    try {
      const decision = decisions.find(d => d.id === decisionId);
      if (!decision) return;

      const votes = decision.contributor_votes || {};
      const hasVoted = votes[userId];

      const updatedVotes = {
        ...votes,
        [userId]: !hasVoted
      };

      const { error } = await supabase
        .from('sprint_decisions')
        .update({ contributor_votes: updatedVotes })
        .eq('id', decisionId);

      if (error) throw error;

      // Optimistic update
      setDecisions(prev => prev.map(d =>
        d.id === decisionId ? { ...d, contributor_votes: updatedVotes } : d
      ));

      // Check if this is first vote
      const totalVotes = Object.values(votes).filter(Boolean).length;
      if (totalVotes === 0 && !hasVoted) {
        setMilestone('🗳️ First vote cast!');
        setTimeout(() => setMilestone(''), 3000);
      }
    } catch (error: any) {
      toast({
        title: "Vote failed",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const handleCompleteStage = async () => {
    // Check if at least one vote has been cast
    const hasAnyVotes = decisions.some(d => 
      Object.values(d.contributor_votes || {}).filter(Boolean).length > 0
    );

    if (!hasAnyVotes) {
      toast({
        title: "No votes cast",
        description: "Please vote on at least one decision before proceeding",
        variant: "destructive"
      });
      return;
    }

    try {
      // Mark top-voted decisions as selected
      const sortedDecisions = [...decisions].sort((a, b) => {
        const votesA = Object.values(a.contributor_votes || {}).filter(Boolean).length;
        const votesB = Object.values(b.contributor_votes || {}).filter(Boolean).length;
        return votesB - votesA;
      });

      const topDecision = sortedDecisions[0];
      if (topDecision) {
        await supabase
          .from('sprint_decisions')
          .update({ is_selected: true })
          .eq('id', topDecision.id);
      }

      const { error } = await supabase
        .from('prompt_architecture_sprints')
        .update({ decide_stage_completed: true })
        .eq('id', sprintId);

      if (error) throw error;

      onComplete();
    } catch (error: any) {
      toast({
        title: "Failed to complete stage",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  if (loading || generating) {
    return (
      <Card className="p-6">
        <div className="space-y-4">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-32 w-full" />
        </div>
      </Card>
    );
  }

  // Get top voted decision
  const topDecision = [...decisions].sort((a, b) => {
    const votesA = Object.values(a.contributor_votes || {}).filter(Boolean).length;
    const votesB = Object.values(b.contributor_votes || {}).filter(Boolean).length;
    return votesB - votesA;
  })[0];

  const topVoteCount = topDecision 
    ? Object.values(topDecision.contributor_votes || {}).filter(Boolean).length 
    : 0;

  return (
    <div className="space-y-4">
      <GibletGuide stage="decide" milestone={milestone} />
      
      <Card className="p-6">
        <div className="space-y-6">
          <div>
            <h3 className="text-xl font-bold mb-2">Stage 2: Decide</h3>
            <p className="text-muted-foreground">
              AI has analyzed your Inform content. Vote on the best strategic decisions to guide execution.
            </p>
          </div>

          <div className="space-y-4">
            {decisions.length === 0 ? (
              <EmptyState
                icon={VoteIcon}
                title="No Decisions Yet"
                description="Complete the Inform stage to generate AI-powered strategic decisions"
              />
            ) : decisions.map((decision) => {
              const voteCount = Object.values(decision.contributor_votes || {}).filter(Boolean).length;
              const hasVoted = decision.contributor_votes?.[userId];
              const isTopChoice = voteCount > 0 && voteCount === topVoteCount;
              const voters = Object.keys(decision.contributor_votes || {}).filter(
                id => decision.contributor_votes[id]
              );

              return (
                <Card 
                  key={decision.id} 
                  className={`p-4 transition-all ${
                    hasVoted ? 'border-primary bg-primary/5' : ''
                  } ${isTopChoice && voteCount > 0 ? 'ring-2 ring-primary/50' : ''}`}
                >
                  <div className="space-y-3">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h4 className="font-semibold">{decision.decision_text}</h4>
                          {isTopChoice && voteCount > 0 && (
                            <Badge className="flex items-center gap-1">
                              <Trophy className="w-3 h-3" />
                              Top Choice
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground mb-3">{decision.rationale}</p>
                        
                        {/* AI Confidence Meter */}
                        <div className="space-y-1">
                          <div className="flex items-center justify-between text-xs">
                            <span className="text-muted-foreground">AI Confidence</span>
                            <Badge variant="secondary" className="flex items-center gap-1">
                              <Sparkles className="w-3 h-3" />
                              {Math.round(decision.ai_confidence_score * 100)}%
                            </Badge>
                          </div>
                          <Progress value={decision.ai_confidence_score * 100} className="h-2" />
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center justify-between pt-2 border-t">
                      <div className="flex items-center gap-3">
                        <div className="text-sm text-muted-foreground flex items-center gap-1">
                          <Users className="w-4 h-4" />
                          {voteCount} {voteCount === 1 ? 'vote' : 'votes'}
                        </div>
                        {voters.length > 0 && (
                          <div className="flex -space-x-2">
                            {voters.slice(0, 3).map((voterId, idx) => (
                              <Avatar key={voterId} className="h-6 w-6 border-2 border-background">
                                <AvatarFallback className="text-xs">
                                  {voterId === userId ? 'You' : `U${idx + 1}`}
                                </AvatarFallback>
                              </Avatar>
                            ))}
                            {voters.length > 3 && (
                              <div className="h-6 w-6 rounded-full border-2 border-background bg-muted flex items-center justify-center text-xs">
                                +{voters.length - 3}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      <Button
                        variant={hasVoted ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => handleVote(decision.id)}
                        className="flex items-center gap-2"
                      >
                        <ThumbsUp className="w-4 h-4" />
                        {hasVoted ? 'Voted' : 'Vote'}
                      </Button>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>

          <div className="pt-4 border-t">
            <Button onClick={handleCompleteStage} size="lg" className="w-full">
              Complete Decide Stage →
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};
